import { z } from 'zod'

// Communications in Hub, Phase 1 (ADR 0034; docs/architecture/
// BRE-Communications-Plan.md §6): one consent ledger and one suppression
// list for every BRE brand and app. Nothing here sends anything.
//
// The key of consent is the ADDRESS (an email address or a phone number,
// normalized), not a person: the person who unsubscribes may never have
// signed in, and email and phone are evidence, never identity (spec §4 rule
// 7). Hub keeps one `comms_address` row per normalized address. A person's
// Communications panel shows the addresses on their contact points; Hub
// never merges or matches people because two consents share an address.

// ------------------------------------------------------------- vocabulary

// Email, text (Phase 3) and myBRE push (Phase 4, ADR 0037).
export const COMMS_CHANNELS = ['email', 'sms', 'push'] as const
export type CommsChannel = (typeof COMMS_CHANNELS)[number]
export const COMMS_CHANNEL_LABELS: Record<CommsChannel, string> = { email: 'Email', sms: 'Text message', push: 'myBRE push' }
// The channel's name inside a sentence ("by email", "by myBRE push").
export const commsChannelWord = (channel: CommsChannel): string => (channel === 'push' ? 'myBRE push' : COMMS_CHANNEL_LABELS[channel].toLowerCase())
export const isCommsChannel = (value: unknown): value is CommsChannel => typeof value === 'string' && (COMMS_CHANNELS as readonly string[]).includes(value)

// A push goes to a myBRE account: its contact point is the account, an
// address of kind 'mybre' whose value is the BRE Auth subject (ADR 0037).
export const COMMS_ADDRESS_KINDS = ['email', 'phone', 'mybre'] as const
export type CommsAddressKind = (typeof COMMS_ADDRESS_KINDS)[number]
export const addressKindOf = (channel: CommsChannel): CommsAddressKind => (channel === 'email' ? 'email' : channel === 'sms' ? 'phone' : 'mybre')

// Purposes and brands are data (comms_purpose, comms_brand), seeded with the
// plan's provisional defaults and editable by comms.manage. Their keys are
// short slugs.
export const COMMS_KEY = /^[a-z0-9][a-z0-9-]{0,38}[a-z0-9]$/

export const CONSENT_ACTIONS = ['grant', 'withdraw', 'confirm'] as const
export type ConsentAction = (typeof CONSENT_ACTIONS)[number]

export const CONSENT_STATES = ['granted', 'pending_confirmation', 'withdrawn'] as const
export type ConsentStateValue = (typeof CONSENT_STATES)[number]
// What a reader gets for an address with no row at all: unknown is no
// (plan §6.4 rule 2).
export type ConsentAnswer = ConsentStateValue | 'none'
export const CONSENT_STATE_LABELS: Record<ConsentAnswer, string> = {
  granted: 'Subscribed',
  pending_confirmation: 'Waiting for confirmation',
  withdrawn: 'Unsubscribed',
  none: 'No consent on record',
}

// The apps whose consent facts Hub imports (contracts/src/source-consent.ts).
export const CONSENT_IMPORT_APPS = ['bre-ticketing', 'craft-show', 'riverfront-broadcast'] as const
export type ConsentImportApp = (typeof CONSENT_IMPORT_APPS)[number]
export const CONSENT_IMPORT_APP_LABELS: Record<ConsentImportApp, string> = { 'bre-ticketing': 'BTownTix', 'craft-show': 'Craft Show', 'riverfront-broadcast': 'Riverfront Broadcast' }
export const isConsentImportApp = (value: unknown): value is ConsentImportApp => typeof value === 'string' && (CONSENT_IMPORT_APPS as readonly string[]).includes(value)

// Where a consent event came from. `import:<app>` for the imports.
export const CONSENT_SOURCES = ['hub_form', 'preference_center', 'one_click', 'sms_keyword', 'staff_recorded', 'mybre'] as const
export type ConsentSource = (typeof CONSENT_SOURCES)[number] | `import:${ConsentImportApp}`
export const CONSENT_SOURCE_LABELS: Record<string, string> = {
  hub_form: 'Hub form',
  preference_center: 'Preference center',
  one_click: 'One-click unsubscribe',
  sms_keyword: 'Text keyword',
  staff_recorded: 'Recorded by staff',
  mybre: 'myBRE',
  'import:bre-ticketing': 'Imported from BTownTix',
  'import:craft-show': 'Imported from Craft Show',
  'import:riverfront-broadcast': 'Imported from Riverfront Broadcast',
}

export const SUPPRESSION_REASONS = ['hard_bounce', 'complaint', 'sms_stop', 'invalid_number', 'manual', 'legal_request', 'provider'] as const
export type SuppressionReason = (typeof SUPPRESSION_REASONS)[number]
export const SUPPRESSION_REASON_LABELS: Record<SuppressionReason, string> = {
  hard_bounce: 'Hard bounce',
  complaint: 'Spam complaint',
  sms_stop: 'Replied STOP',
  invalid_number: 'Not a valid number',
  manual: 'Added by staff',
  legal_request: 'Legal or privacy request',
  provider: 'Suppressed by the provider',
}
// What staff may choose by hand; the others come from providers and imports.
export const STAFF_SUPPRESSION_REASONS = ['manual', 'legal_request', 'invalid_number', 'hard_bounce', 'complaint'] as const satisfies readonly SuppressionReason[]

// ---------------------------------------------------------- normalization

// The one way Hub writes an address down. Email: trimmed and lowercased.
// Phone: E.164; a 10-digit number (or 11 starting with 1) is taken as US.
// Null when the value can't be an address of that kind.
export function normalizeCommsAddress(kind: CommsAddressKind, raw: string): string | null {
  const value = (raw ?? '').trim()
  if (!value || value.length > 320) return null
  if (kind === 'email') {
    const lower = value.toLowerCase()
    return /^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/.test(lower) ? lower : null
  }
  // A BRE Auth subject, exactly as BRE Auth issued it.
  if (kind === 'mybre') return value.length >= 3 && value.length <= 200 && !/[\s<>"']/.test(value) ? value : null
  const plus = value.startsWith('+')
  const digits = value.replace(/\D/g, '')
  if (!plus && digits.length === 10) return `+1${digits}`
  if (!plus && digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  if (plus && digits.length >= 8 && digits.length <= 15 && !digits.startsWith('0')) return `+${digits}`
  return null
}

// Hub's contact_point rows keep a US phone as its 10 digits (people sync,
// ADR 0009); this is the same number as an E.164 address, or null.
export function contactPointToAddress(kind: 'email' | 'phone', valueNormalized: string): string | null {
  return normalizeCommsAddress(kind, valueNormalized)
}

// Shown on the public pages: enough to recognize, not to harvest.
export function maskAddress(kind: CommsAddressKind, value: string): string {
  if (kind === 'email') {
    const [local = '', domain = ''] = value.split('@')
    return `${local.slice(0, 1)}${'•'.repeat(Math.max(2, Math.min(6, local.length - 1)))}@${domain}`
  }
  if (kind === 'mybre') return 'This myBRE account'
  return `•••-•••-${value.slice(-4)}`
}

// ------------------------------------------------------ deriving the state

// The current answer for one (address, channel, purpose, brand) is derived
// from its events, never stored on its own (consent_state is a cache that
// Platform Core rebuilds in SQL with exactly these rules; the tests hold
// the two together):
//
//   - Events are ordered by when they happened (occurredAt), then a
//     withdrawal before a grant at the same instant (withdrawals win ties),
//     then when Hub recorded them, then id.
//   - The latest DECISIVE event decides: a withdrawal, a grant that needs no
//     confirmation, or a confirmation.
//   - A grant that waits for confirmation (double opt-in) never takes away
//     an effective grant; it shows as "waiting for confirmation" only when
//     it is later than the latest decisive event.

export type ConsentEventForDerive = { id: string; action: ConsentAction; pendingConfirmation: boolean; occurredAt: string; recordedAt: string }

const sortKey = (e: ConsentEventForDerive) => `${e.occurredAt}|${e.action === 'withdraw' ? '2' : '1'}|${e.recordedAt}|${e.id}`

export function deriveConsentState(events: ConsentEventForDerive[]): { state: ConsentStateValue; decidingEventId: string } | null {
  let decisive: ConsentEventForDerive | null = null
  let pending: ConsentEventForDerive | null = null
  for (const e of events) {
    const isPending = e.action === 'grant' && e.pendingConfirmation
    if (isPending) {
      if (!pending || sortKey(e) > sortKey(pending)) pending = e
    } else if (!decisive || sortKey(e) > sortKey(decisive)) decisive = e
  }
  if (decisive && decisive.action !== 'withdraw') return { state: 'granted', decidingEventId: decisive.id }
  if (pending && (!decisive || sortKey(pending) > sortKey(decisive))) return { state: 'pending_confirmation', decidingEventId: pending.id }
  if (decisive) return { state: 'withdrawn', decidingEventId: decisive.id }
  return null
}

export type SuppressionEventForDerive = { id: string; action: 'suppress' | 'release'; occurredAt: string; recordedAt: string }

// The latest event decides; a suppression wins a tie with a release.
export function deriveSuppression(events: SuppressionEventForDerive[]): { suppressed: boolean; decidingEventId: string } | null {
  const key = (e: SuppressionEventForDerive) => `${e.occurredAt}|${e.action === 'suppress' ? '2' : '1'}|${e.recordedAt}|${e.id}`
  let latest: SuppressionEventForDerive | null = null
  for (const e of events) if (!latest || key(e) > key(latest)) latest = e
  return latest ? { suppressed: latest.action === 'suppress', decidingEventId: latest.id } : null
}

// What a sender may do (plan §6.4): suppression beats any grant; a purpose
// that needs consent needs an effective grant ("unknown is no"); a
// transactional message is the owning app's decision, so Hub reports the
// suppression but doesn't forbid it.
export function consentDecision(input: { needsConsent: boolean; transactional: boolean; consent: ConsentAnswer; suppressed: boolean }): { allowed: boolean; reason: 'suppressed' | 'no_consent' | 'pending_confirmation' | 'withdrawn' | 'consent' | 'no_consent_needed' | 'transactional' } {
  if (input.transactional) return { allowed: true, reason: 'transactional' }
  if (input.suppressed) return { allowed: false, reason: 'suppressed' }
  if (!input.needsConsent) return { allowed: true, reason: 'no_consent_needed' }
  if (input.consent === 'granted') return { allowed: true, reason: 'consent' }
  if (input.consent === 'pending_confirmation') return { allowed: false, reason: 'pending_confirmation' }
  if (input.consent === 'withdrawn') return { allowed: false, reason: 'withdrawn' }
  return { allowed: false, reason: 'no_consent' }
}

// ---------------------------------------------- preference and one-click links

// A link Hub puts in a message (Phase 2) or a hub.admin makes for testing:
//   https://forms.bre.live/p/<token>   the preference center
//   https://forms.bre.live/u/<token>   the RFC 8058 one-click target
// The token names one address (by Hub's id, never the address itself), one
// channel, optionally one brand, and when it stops working; it is an HMAC
// under a key derived from HUB_CORE_ACTOR_SECRET, so nothing is stored
// before it is used and editing one link can't reach another address.
//
//   v1.<addressId>.<channel>.<brand or ->.<expiresAt unix seconds>.<signature>

export const PREFERENCE_TOKEN_VERSION = 'v1'
const TOKEN = /^v1\.(cad_[A-Za-z0-9]{8,40})\.(email|sms|push)\.([a-z0-9-]{1,40}|-)\.(\d{9,11})\.([A-Za-z0-9_-]{32})$/

const encoder = new TextEncoder()

function base64url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '')
}

async function hmacB64(key: string, message: string): Promise<string> {
  const k = await crypto.subtle.importKey('raw', encoder.encode(key), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  return base64url(new Uint8Array(await crypto.subtle.sign('HMAC', k, encoder.encode(message))))
}

function equalStrings(a: string, b: string): boolean {
  if (a.length !== b.length) return false
  let diff = 0
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i)
  return diff === 0
}

export type PreferenceToken = { addressId: string; channel: CommsChannel; brandKey: string | null; expiresAt: number }

export async function signPreferenceToken(secret: string, token: PreferenceToken): Promise<string> {
  if (!secret || secret.length < 32) throw new Error('The token secret must be at least 32 characters')
  const body = `${PREFERENCE_TOKEN_VERSION}.${token.addressId}.${token.channel}.${token.brandKey ?? '-'}.${token.expiresAt}`
  return `${body}.${(await hmacB64(`hub-comms-preference-v1:${secret}`, body)).slice(0, 32)}`
}

export type PreferenceTokenCheck = { ok: true; token: PreferenceToken } | { ok: false; reason: 'malformed' | 'bad_signature' | 'expired' }

export async function verifyPreferenceToken(secret: string, raw: string, nowMs = Date.now()): Promise<PreferenceTokenCheck> {
  const match = TOKEN.exec(raw ?? '')
  if (!match) return { ok: false, reason: 'malformed' }
  const [, addressId, channel, brand, exp, signature] = match
  const body = raw.slice(0, raw.length - 33)
  const expected = (await hmacB64(`hub-comms-preference-v1:${secret}`, body)).slice(0, 32)
  if (!equalStrings(expected, signature!)) return { ok: false, reason: 'bad_signature' }
  const expiresAt = Number(exp)
  if (expiresAt * 1000 <= nowMs) return { ok: false, reason: 'expired' }
  return { ok: true, token: { addressId: addressId!, channel: channel as CommsChannel, brandKey: brand === '-' ? null : brand!, expiresAt } }
}

// The public preference center's own actor toward Platform Core: a system
// subject with no person, which core lets reach /v1/public/comms/* only.
export const COMMS_PUBLIC_SUB = 'system:comms-public'
// The scheduled import's actor, which core lets reach the import routes only.
export const COMMS_IMPORT_SUB = 'system:comms-import'

// -------------------------------------------- the app-facing consent read

// Hub's app-facing contract (plan §10, Phase 1): an app asks, server to
// server, whether it may send a message of a purpose and brand to an
// address, before it sends it. No app calls it in Phase 1.
//
//   GET  /internal/comms/v1/consent?channel=email&purpose=marketing&brand=btowntix&address=a@b.c[&address=...]
//   POST /internal/comms/v1/consent   {"channel","purpose","brand","addresses":[...]}
//
// Up to CONSENT_READ_MAX_ADDRESSES addresses per request. POST is preferred:
// an address in a query string ends up in logs.
//
// Who is asking: each app has its own secret (HUB_CONSENT_READ_KEYS on Hub's
// web Worker, a JSON object of app id to secret, each at least 32
// characters). The request is signed like the volunteer and board channels:
//
//   canonical = "bre-hub-consent-v1" \n METHOD \n path?query \n app \n
//               timestamp (unix seconds) \n nonce \n sha256-hex(body)
//   signature = base64url(HMAC-SHA256(secret, canonical))   // no padding
//
// Headers: x-hub-app, x-hub-app-timestamp, x-hub-app-nonce,
// x-hub-app-signature. A request more than 60 seconds from Hub's clock, a
// nonce seen before, or a bad signature is one 401. Each app gets
// CONSENT_READ_RATE_PER_MINUTE requests a minute; more is 429.
//
// The answer never says why an address was suppressed beyond the reason
// code, and never names a person.

export const CONSENT_READ_PREFIX = '/internal/comms/v1'
export const CONSENT_READ_PATH = `${CONSENT_READ_PREFIX}/consent`
export const CONSENT_SIGNATURE_VERSION = 'bre-hub-consent-v1'
export const CONSENT_SIGNATURE_MAX_SKEW_SECONDS = 60
export const CONSENT_NONCE_TTL_SECONDS = 180
export const CONSENT_READ_RATE_PER_MINUTE = 600
export const CONSENT_READ_MAX_ADDRESSES = 100
export const CONSENT_READ_APPS = ['bre-ticketing', 'bre-loyalty', 'pos', 'booking', 'craft-show', 'riverfront-broadcast', 'brewfest', 'bre-auth'] as const
export type ConsentReadApp = (typeof CONSENT_READ_APPS)[number]
export const CONSENT_HEADERS = { app: 'x-hub-app', timestamp: 'x-hub-app-timestamp', nonce: 'x-hub-app-nonce', signature: 'x-hub-app-signature' } as const
// The actor the web Worker signs for core on this channel.
export const CONSENT_APP_SUB_PREFIX = 'comms-app:'

export async function sha256HexOf(body: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(body))
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function consentCanonical(input: { method: string; pathWithQuery: string; app: string; timestamp: string; nonce: string; bodySha256: string }): string {
  return [CONSENT_SIGNATURE_VERSION, input.method.toUpperCase(), input.pathWithQuery, input.app, input.timestamp, input.nonce, input.bodySha256].join('\n')
}

// What an app does (each app keeps its own copy; the test vector in
// comms.test.ts keeps them honest).
export async function signConsentRequest(input: { secret: string; app: string; method: string; pathWithQuery: string; body?: string; now?: number; nonce?: string }): Promise<Record<string, string>> {
  if (!input.secret || input.secret.length < 32) throw new Error('The consent read secret must be at least 32 characters')
  const timestamp = String(Math.floor((input.now ?? Date.now()) / 1000))
  const nonce = input.nonce ?? base64url(crypto.getRandomValues(new Uint8Array(18)))
  const signature = await hmacB64(input.secret, consentCanonical({ method: input.method, pathWithQuery: input.pathWithQuery, app: input.app, timestamp, nonce, bodySha256: await sha256HexOf(input.body ?? '') }))
  return { [CONSENT_HEADERS.app]: input.app, [CONSENT_HEADERS.timestamp]: timestamp, [CONSENT_HEADERS.nonce]: nonce, [CONSENT_HEADERS.signature]: signature }
}

export type ConsentSignatureFailure = 'missing' | 'malformed' | 'unknown_app' | 'stale' | 'bad_signature'

// Checks everything but the nonce's novelty (the caller keeps the nonces).
export async function verifyConsentRequest(input: {
  secrets: Partial<Record<string, string>>
  method: string
  pathWithQuery: string
  headers: { get(name: string): string | null }
  body: string
  now?: number
}): Promise<{ ok: true; app: ConsentReadApp; nonce: string } | { ok: false; reason: ConsentSignatureFailure }> {
  const app = input.headers.get(CONSENT_HEADERS.app)
  const ts = input.headers.get(CONSENT_HEADERS.timestamp)
  const nonce = input.headers.get(CONSENT_HEADERS.nonce)
  const signature = input.headers.get(CONSENT_HEADERS.signature)
  if (!app || !ts || !nonce || !signature) return { ok: false, reason: 'missing' }
  if (!/^\d{9,11}$/.test(ts) || !/^[A-Za-z0-9_-]{16,64}$/.test(nonce) || !/^[A-Za-z0-9_-]{43}$/.test(signature)) return { ok: false, reason: 'malformed' }
  if (!(CONSENT_READ_APPS as readonly string[]).includes(app)) return { ok: false, reason: 'unknown_app' }
  const secret = input.secrets[app]
  if (!secret || secret.length < 32) return { ok: false, reason: 'unknown_app' }
  const now = Math.floor((input.now ?? Date.now()) / 1000)
  if (Math.abs(now - Number(ts)) > CONSENT_SIGNATURE_MAX_SKEW_SECONDS) return { ok: false, reason: 'stale' }
  const expected = await hmacB64(secret, consentCanonical({ method: input.method, pathWithQuery: input.pathWithQuery, app, timestamp: ts, nonce, bodySha256: await sha256HexOf(input.body) }))
  if (!equalStrings(expected, signature)) return { ok: false, reason: 'bad_signature' }
  return { ok: true, app: app as ConsentReadApp, nonce }
}

// The secrets, from the web Worker's HUB_CONSENT_READ_KEYS. Anything that
// isn't a JSON object of known app ids to strings of 32+ characters is
// dropped (and an empty map turns the channel off).
export function parseConsentReadKeys(raw: string | undefined): Partial<Record<ConsentReadApp, string>> {
  if (!raw) return {}
  try {
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {}
    const out: Partial<Record<ConsentReadApp, string>> = {}
    for (const [app, secret] of Object.entries(parsed as Record<string, unknown>)) {
      if ((CONSENT_READ_APPS as readonly string[]).includes(app) && typeof secret === 'string' && secret.length >= 32) out[app as ConsentReadApp] = secret
    }
    return out
  } catch {
    return {}
  }
}

export const ConsentCheckRequestSchema = z.object({
  channel: z.enum(COMMS_CHANNELS),
  purpose: z.string().regex(COMMS_KEY),
  brand: z.string().regex(COMMS_KEY),
  addresses: z.array(z.string().min(1).max(320)).min(1).max(CONSENT_READ_MAX_ADDRESSES),
  // For an app sending commercial email itself: each answer also carries the
  // recipient's unsubscribe and preference links. Email only.
  withLinks: z.boolean().optional(),
})
export type ConsentCheckRequest = z.infer<typeof ConsentCheckRequestSchema>

export type ConsentCheckResult = {
  // As the app sent it, so it can match its answers up.
  address: string
  // Null when it isn't an address of the channel's kind.
  normalized: string | null
  consent: ConsentAnswer
  // When the deciding event happened, if any.
  consentAt: string | null
  suppressed: boolean
  suppressionReason: SuppressionReason | null
  allowed: boolean
  // With withLinks: core's signed token, which the web Worker turns into these
  // URLs. Null without a forms host: an app must not send without one.
  linkToken?: string
  unsubscribeUrl?: string | null
  preferenceUrl?: string | null
  reason: ReturnType<typeof consentDecision>['reason'] | 'invalid_address'
}

export type ConsentCheckResponse = {
  version: 1
  asOf: string
  channel: CommsChannel
  purpose: string
  brand: string
  // False when the purpose or brand isn't one Hub knows (then nothing is allowed).
  known: boolean
  results: ConsentCheckResult[]
}

// --------------------------------------------------------- staff-facing shapes

export type CommsBrand = {
  key: string
  name: string
  legalName: string
  postalAddress: string
  preferenceLabel: string
  description: string | null
  sortOrder: number
  active: boolean
  updatedAt: string
  updatedByName: string | null
}

export type CommsPurpose = {
  key: string
  name: string
  description: string
  needsConsent: boolean
  transactional: boolean
  // Shown on the preference center (only purposes that need consent are).
  publicChoice: boolean
  sortOrder: number
  active: boolean
}

export type ConsentStatement = {
  id: string
  brandKey: string
  purposeKey: string
  channel: CommsChannel
  version: number
  text: string
  createdAt: string
  createdByName: string | null
  // Once an event cites it, it never changes.
  firstUsedAt: string | null
  retiredAt: string | null
}

export type CommsSettings = {
  doubleOptInPublicForms: boolean
  preferenceLinkDays: number
  formConsentBrandKey: string
  updatedAt: string | null
  updatedByName: string | null
}

export type ConsentImportScope = {
  app: ConsentImportApp
  scope: string
  action: 'import' | 'ignore'
  brandKey: string | null
  purposeKey: string | null
  allowGrants: boolean
  note: string | null
}

export type ConsentEventView = {
  id: string
  channel: CommsChannel
  purposeKey: string
  brandKey: string
  action: ConsentAction
  pendingConfirmation: boolean
  source: string
  sourceRef: string | null
  personId: string | null
  personName: string | null
  statementText: string | null
  statementVersion: string | null
  evidenceNote: string | null
  ipHashed: boolean
  userAgentHashed: boolean
  actorName: string | null
  attested: boolean
  occurredAt: string
  occurredAtKnown: boolean
  recordedAt: string
}

export type SuppressionEventView = {
  id: string
  channel: CommsChannel
  // An SMS STOP's Messaging Service (ADR 0036); null = the whole channel.
  scope: string | null
  action: 'suppress' | 'release'
  reason: SuppressionReason | null
  source: string
  sourceRef: string | null
  note: string | null
  actorName: string | null
  occurredAt: string
  occurredAtKnown: boolean
  recordedAt: string
}

export type ConsentStateRow = { channel: CommsChannel; purposeKey: string; brandKey: string; state: ConsentStateValue; decidedAt: string; decidingEventId: string }
// scope: null for the whole channel, or the Messaging Service an SMS STOP
// applies to (ADR 0036), with its name.
export type SuppressionRow = { channel: CommsChannel; scope: string | null; scopeName: string | null; reason: SuppressionReason; since: string; decidingEventId: string }

export type CommsAddressDetail = {
  address: { id: string; kind: CommsAddressKind; value: string; createdAt: string }
  states: ConsentStateRow[]
  suppressions: SuppressionRow[]
  events: ConsentEventView[]
  suppressionEvents: SuppressionEventView[]
  // Hub people with this address on a contact point (evidence: two people
  // may share one). Only with people.read.
  people: { personId: string; displayName: string; verified: boolean }[] | null
}

export type PersonCommsPanel = {
  addresses: {
    id: string | null
    kind: CommsAddressKind
    value: string
    verified: boolean
    states: ConsentStateRow[]
    suppressions: SuppressionRow[]
  }[]
  // Events recorded for exactly this person (by subject or by staff on this
  // page), newest first, across every address.
  recent: (ConsentEventView & { address: string })[]
  // Every message to this person or their addresses, from any source
  // (ADR 0035), newest first.
  messages: PersonMessage[]
  // Texts the person's numbers sent to Hub's numbers (ADR 0036).
  inbound: { id: string; keywordClass: 'stop' | 'start' | 'help' | 'other'; keyword: string | null; body: string | null; receivedAt: string; needsReview: boolean; from: string; serviceName: string | null }[]
  // Journeys the person is or was in (ADR 0037), newest first.
  journeys: PersonJourney[]
}

export type PersonJourney = {
  enrollmentId: string
  journeyId: string
  journeyName: string
  version: number
  status: 'active' | 'completed' | 'exited'
  stepIndex: number
  nextRunAt: string | null
  exitReason: string | null
  enrolledAt: string
  finishedAt: string | null
  eventName: string | null
  sent: number
}

export type PersonMessage = {
  id: string
  source: string
  channel: 'email' | 'sms' | 'push'
  campaignId: string | null
  campaignName: string | null
  brandKey: string
  purposeKey: string
  to: string | null
  subject: string | null
  status: string
  statusReason: string | null
  provider: string | null
  test: boolean
  createdAt: string
  deliveredAt: string | null
}

export type ConsentImportStatus = {
  app: ConsentImportApp
  cursor: string | null
  lastRunAt: string | null
  lastFullAt: string | null
  lastStatus: 'ok' | 'partial' | 'error' | 'not_connected' | null
  lastMessage: string | null
  counts: Record<string, number>
}

export type ConsentImportPageResult = { applied: number; counts: Record<string, number> }

// ----------------------------------------------------- staff request bodies

export const RecordConsentSchema = z.object({
  address: z.string().trim().min(3).max(320),
  kind: z.enum(COMMS_ADDRESS_KINDS),
  channel: z.enum(COMMS_CHANNELS),
  brandKey: z.string().regex(COMMS_KEY),
  purposeKey: z.string().regex(COMMS_KEY),
  action: z.enum(['grant', 'withdraw']),
  // A grant cites the statement the person agreed to (its exact wording).
  statementId: z.string().min(1).max(100).optional(),
  // How it was taken: "Signed paper form at the box office, filed in ...".
  evidenceNote: z.string().trim().min(10).max(2000),
  // The staff member attests the person gave or withdrew it as recorded.
  attest: z.literal(true),
  // When the person gave it; not in the future. Defaults to now.
  occurredAt: z.string().datetime().optional(),
  // The person page it was recorded from, when it was.
  personId: z.string().min(1).max(100).optional(),
})
export type RecordConsentBody = z.infer<typeof RecordConsentSchema>

export const SuppressionChangeSchema = z.object({
  address: z.string().trim().min(3).max(320),
  kind: z.enum(COMMS_ADDRESS_KINDS),
  channel: z.enum(COMMS_CHANNELS),
  action: z.enum(['suppress', 'release']),
  reason: z.enum(STAFF_SUPPRESSION_REASONS).optional(),
  note: z.string().trim().min(5).max(2000),
})
export type SuppressionChangeBody = z.infer<typeof SuppressionChangeSchema>

// Phone numbers and emails from a search box: which kind it looks like.
export function guessAddressKind(query: string): CommsAddressKind | null {
  const q = query.trim()
  if (q.includes('@')) return 'email'
  const digits = q.replace(/\D/g, '')
  if (digits.length >= 10 && /^[+\d\s().-]+$/.test(q)) return 'phone'
  return null
}
